import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import WaybackArchiveButton from "./WaybackArchiveButton";
import { BlogBook, useGetMyBooksQuery } from "../../services/rtkApi/blogApi";

/**
 * The author's books at `/blog/manage/books`. Each row shows how many chapters
 * the book has and whether its table of contents is public, with the archive
 * button alongside — the same shape as the posts dashboard.
 */
const BooksManagePage: React.FC = () => {
  const { data, isLoading, isError } = useGetMyBooksQuery();
  const books = data?.books ?? [];

  return (
    <PageContainer>
      <Inner>
        <Header>
          <div>
            <Title>your books</Title>
            <Subtitle>topics you are filling in one chapter at a time</Subtitle>
          </div>
          <NewLink to="/blog/books/new">+ start a book</NewLink>
        </Header>

        <Tabs>
          <TabLink to="/blog/manage">posts</TabLink>
          <TabLink to="/blog/manage/books" $active>
            books
          </TabLink>
        </Tabs>

        {isLoading && <Muted>loading books…</Muted>}
        {isError && <Muted>could not load your books</Muted>}
        {!isLoading && !isError && books.length === 0 && (
          <EmptyState>
            no books yet. <Link to="/blog/books/new">start one</Link> — you can add
            chapters whenever you write them.
          </EmptyState>
        )}

        <List>
          {books.map((book) => (
            <BookRow key={book._id} book={book} />
          ))}
        </List>

        <BackLink to="/blog">&larr; back to the blog</BackLink>
      </Inner>
    </PageContainer>
  );
};

const BookRow: React.FC<{ book: BlogBook }> = ({ book }) => {
  const isPublished = book.status === "published";

  return (
    <Card>
      <CardMain>
        <CardMeta>
          <StatusPill $published={isPublished}>
            {isPublished ? "published" : "draft"}
          </StatusPill>
          {book.complete && <CompletePill>complete</CompletePill>}
          <MetaText>
            {book.chapterCount} {book.chapterCount === 1 ? "chapter" : "chapters"}
          </MetaText>
          {book.updatedAt && (
            <MetaText>· updated {new Date(book.updatedAt).toLocaleDateString()}</MetaText>
          )}
        </CardMeta>

        <CardTitle>{book.title}</CardTitle>
        {(book.subtitle || book.excerpt) && (
          <CardExcerpt>{book.subtitle || book.excerpt}</CardExcerpt>
        )}

        <CardActions>
          <ActionLink to={`/blog/manage/books/${book._id}`}>edit &amp; order</ActionLink>
          {isPublished && <ActionLink to={`/blog/books/${book.slug}`}>view</ActionLink>}
          <ActionLink to="/blog/new">+ add a chapter</ActionLink>
        </CardActions>
      </CardMain>

      <CardSide>
        <WaybackArchiveButton
          articleId={book._id}
          kind="book"
          initialWayback={book.wayback}
          compact
          disabled={!isPublished}
          disabledReason={!isPublished ? "publish it first to archive" : undefined}
        />
      </CardSide>
    </Card>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  color: #222;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 2px 2px 5px #3c3c3cae;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 6px 0 0;
  font-style: italic;
`;

const NewLink = styled(Link)`
  padding: 10px 20px;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid #466a5d;
  background: #9beece;
  color: #222;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.5);

  &:hover {
    box-shadow: 3px 4px rgba(0, 0, 0, 0.5);
  }

  &:active {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.5);
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 6px 16px;
  font-size: 0.85rem;
  text-decoration: none;
  border: 1px solid ${(p) => (p.$active ? "#466a5d" : "#ccc")};
  background: ${(p) => (p.$active ? "#9beece" : "white")};
  color: ${(p) => (p.$active ? "#222" : "#666")};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 28px;
`;

const Card = styled.div`
  display: grid;
  /* the side column is capped: an expanded archive button must never squeeze
     the title column down to one word per line */
  grid-template-columns: minmax(0, 1fr) minmax(0, 240px);
  gap: 18px;
  background: white;
  border: 1px solid #ccc;
  padding: 18px 20px;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.15);

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const CardMain = styled.div`
  min-width: 0;
`;

const CardSide = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 0;
  padding-top: 2px;

  @media (max-width: 820px) {
    border-top: 1px solid #eee;
    padding-top: 12px;
  }
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const StatusPill = styled.span<{ $published: boolean }>`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border: 1px solid ${(p) => (p.$published ? "#466a5d" : "#b58b2a")};
  background: ${(p) => (p.$published ? "#d4f9eb" : "#fdf1d3")};
  color: #333;
`;

const CompletePill = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border: 1px solid #6a4d8a;
  background: #f3edfa;
  color: #6a4d8a;
`;

const MetaText = styled.span`
  font-size: 0.78rem;
  color: #888;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  color: #222;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const CardExcerpt = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0 0 10px;
  line-height: 1.45;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const ActionLink = styled(Link)`
  font-size: 0.8rem;
  padding: 4px 11px;
  border: 1px solid #ccc;
  background: white;
  color: #444;
  text-decoration: none;
  line-height: 1.6;

  &:hover {
    border-color: #466a5d;
    background: #f2fbf7;
  }
`;

const Muted = styled.p`
  text-align: center;
  color: #888;
  padding: 40px 0;
`;

const EmptyState = styled.p`
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 50px 0;

  a {
    color: #466a5d;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  color: #466a5d;
  text-decoration: none;
  font-size: 0.9rem;
  padding-bottom: 40px;

  &:hover {
    text-decoration: underline;
  }
`;

export default BooksManagePage;
